import { configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import { reduxPersistStorage } from "@/utils/secure_storage";
import authReducer from "@/store/redux/slices/auth/auth_slice";
import userReducer from "@/store/redux/slices/user/user_slice";
import organizationReducer from "@/store/redux/slices/kyc/kyc_setup_slice";
import eventReducer from "@/store/redux/slices/event/event_slice";
import manifestoReducer from "@/store/redux/slices/manifesto/manifesto_slice";
import subEventReducer from "@/store/redux/slices/event/sub_event_slice";
import townhallReducer from "@/store/redux/slices/townhall/townhall_slice";
import fileUploadReducer from "@/store/redux/slices/user/file_upload_slice";
import supportReducer from "@/store/redux/slices/support/support_slice";
import mediaReducer from "@/store/redux/slices/media/media_slice";
import pollsReducer from "@/store/redux/slices/polls_&_survey/polls_&_survey";
import settingsReducer from "@/store/redux/slices/settings/settings_slice";
import userAdminReducer from "@/store/redux/slices/user_admin/user_admin";
import candidateReducer from "@/store/redux/slices/candidate/candidate_slice";
import canvassReducer from "@/store/redux/slices/canvass/canvass_slice";
import roleReducer from "@/store/redux/slices/roles/role_slice";
import fundsReducer from "@/store/redux/slices/finance_management/funds_slice";
import expenseReducer from "@/store/redux/slices/finance_management/expense_slice";
import accountReducer from "@/store/redux/slices/finance_management/account_slice";
import faqReducer from "@/store/redux/slices/FAQ/faq_slice";
import errorReducer from "@/store/redux/slices/error/error_slice";
import errorMiddleware from "@/middleware/errorMiddleware";
import networkMiddleware from "@/middleware/networkMiddleware";
import networkReducer from "@/store/redux/slices/network/network_slice";
import encryptionMiddleware from "@/middleware/encryptionMiddleware";
import searchReducer from "@/store/redux/slices/search/search_slice";
// import offlineMiddleware from '../middleware/offlineMiddleware'; // Import the offline middleware

const isElectron = () => {
  return window && window.process && window.process.type;
};

// Persist config for auth slice - different for web and desktop
const authPersistConfig = {
  key: "auth",
  storage: reduxPersistStorage,
  whitelist: isElectron()
    ? [
        "userInfo",
        "isAuthenticated",
        "isRegistered",
        "otpSent",
        "accessToken",
        "userId",
        "organizationId",
      ]
    : ["userInfo", "isAuthenticated", "isRegistered", "otpSent"],
};

// Separate persist config for KYC setup - persist all step data
const kycPersistConfig = {
  key: "kycSetup",
  storage: reduxPersistStorage,
  whitelist: ["step1", "step2", "step3", "currentStep"],
};

// For Electron: Additional persist configs for offline data
// Only create these if running in Electron
const electronPersistConfigs = isElectron()
  ? {
      townhall: persistReducer(
        {
          key: "townhall",
          storage: reduxPersistStorage,
          whitelist: ["townHalls", "lastFetchTime", "isOfflineData"],
        },
        townhallReducer
      ),

      manifesto: persistReducer(
        {
          key: "manifesto",
          storage: reduxPersistStorage,
          whitelist: ["manifestos", "lastFetchTime", "isOfflineData"],
        },
        manifestoReducer
      ),

      media: persistReducer(
        {
          key: "media",
          storage: reduxPersistStorage,
          whitelist: ["mediaList", "lastFetchTime", "isOfflineData"],
        },
        mediaReducer
      ),

      event: persistReducer(
        {
          key: "event",
          storage: reduxPersistStorage,
          whitelist: ["events", "lastFetchTime", "isOfflineData"],
        },
        eventReducer
      ),

      role: persistReducer(
        {
          key: "role",
          storage: reduxPersistStorage,
          whitelist: ["roles", "lastFetchTime", "isOfflineData"],
        },
        roleReducer
      ),

      funds: persistReducer(
        {
          key: "funds",
          storage: reduxPersistStorage,
          whitelist: ["funds", "lastFetchTime", "isOfflineData"],
        },
        fundsReducer
      ),

      expense: persistReducer(
        {
          key: "expense",
          storage: reduxPersistStorage,
          whitelist: ["expenses", "lastFetchTime", "isOfflineData"],
        },
        expenseReducer
      ),

      account: persistReducer(
        {
          key: "account",
          storage: reduxPersistStorage,
          whitelist: ["accounts", "lastFetchTime", "isOfflineData"],
        },
        accountReducer
      ),

      canvass: persistReducer(
        {
          key: "canvass",
          storage: reduxPersistStorage,
          whitelist: ["canvasses", "lastFetchTime", "isOfflineData"],
        },
        canvassReducer
      ),

      polls: persistReducer(
        {
          key: "polls",
          storage: reduxPersistStorage,
          whitelist: ["polls", "lastFetchTime", "isOfflineData"],
        },
        pollsReducer
      ),

      candidate: persistReducer(
        {
          key: "candidate",
          storage: reduxPersistStorage,
          whitelist: ["candidates", "lastFetchTime", "isOfflineData"],
        },
        candidateReducer
      ),

      userAdmin: persistReducer(
        {
          key: "userAdmin",
          storage: reduxPersistStorage,
          whitelist: [
            "users",
            "fetchedUsers",
            "lastFetchTime",
            "isOfflineData",
          ],
        },
        userAdminReducer
      ),
    }
  : {};

// Create persisted reducers
const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);
const persistedKycReducer = persistReducer(
  kycPersistConfig,
  organizationReducer
);

const isDevEnv = import.meta?.env?.NODE_ENV === "development";

const store = configureStore({
  reducer: {
    auth: persistedAuthReducer,
    user: userReducer,
    organization: persistedKycReducer,
    // Use Electron-specific persisted reducers if in Electron, otherwise use regular reducers
    event: isElectron() ? electronPersistConfigs.event : eventReducer,
    subEvent: subEventReducer,
    manifesto: isElectron()
      ? electronPersistConfigs.manifesto
      : manifestoReducer,
    townhall: isElectron() ? electronPersistConfigs.townhall : townhallReducer,
    fileUpload: fileUploadReducer,
    support: supportReducer,
    media: isElectron() ? electronPersistConfigs.media : mediaReducer,
    polls: isElectron() ? electronPersistConfigs.polls : pollsReducer,
    settings: settingsReducer,
    userAdmin: isElectron()
      ? electronPersistConfigs.userAdmin
      : userAdminReducer,
    candidate: isElectron()
      ? electronPersistConfigs.candidate
      : candidateReducer,
    role: isElectron() ? electronPersistConfigs.role : roleReducer,
    funds: isElectron() ? electronPersistConfigs.funds : fundsReducer,
    expense: isElectron() ? electronPersistConfigs.expense : expenseReducer,
    account: isElectron() ? electronPersistConfigs.account : accountReducer,
    canvass: isElectron() ? electronPersistConfigs.canvass : canvassReducer,
    faq: faqReducer,
    error: errorReducer,
    network: networkReducer,
    search: searchReducer,
  },

  middleware: (getDefaultMiddleware) => {
    const middleware = getDefaultMiddleware({
      // In dev, Redux checks can be costly with large nested/persisted state.
      // Tweak thresholds and ignore heavy branches to avoid noisy warnings.
      immutableCheck: isDevEnv
        ? {
            warnAfter: 128, // raise threshold to reduce warnings during dev
            ignoredPaths: [
              // Large/array-heavy branches
              "event.events",
              "media.mediaList",
              "funds.funds",
              "expense.expenses",
              "userAdmin.users",
              "candidate.candidates",
              "polls.polls",
              "townhall.townHalls",
              "manifesto.manifestos",
            ],
          }
        : false,
      serializableCheck: isDevEnv
        ? {
            ignoredActions: [
              "persist/PERSIST",
              "persist/REHYDRATE",
              "network/statusChanged",
            ],
            ignoredPaths: [
              // Known non-serializable items
              "organization.step1.ownerProfileImage",
              "organization.step2.identificationImage",
              "organization.step3.CACRegistrationCertificateImage",
              "fileUpload.files",
              // Large branches that don't need dev-time serializable checks
              "event.events",
              "media.mediaList",
              "funds.funds",
              "expense.expenses",
              "userAdmin.users",
              "candidate.candidates",
              "polls.polls",
              "townhall.townHalls",
              "manifesto.manifestos",
              ...(isElectron()
                ? ["auth.accessToken", "auth.userId", "auth.organizationId"]
                : []),
            ],
            warnAfter: 128,
          }
        : false,
    });

    return middleware.concat(
      errorMiddleware,
      networkMiddleware,
      encryptionMiddleware
    );
  },
});

export const persistor = persistStore(store);
export default store;
